<template>
  <div class="admin-page">
    <v-row>
      <v-col cols="12" lg="4">
        <v-card class="admin-card" elevation="1">
          <v-card-title class="admin-card-title">
            <v-icon class="mr-2" size="20">mdi-help-circle-outline</v-icon>
            {{
              editing && editingItem
                ? `Editing "${editingItem.text}"`
                : "Create Question"
            }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-text-field
                v-model="text"
                variant="outlined"
                density="comfortable"
                :counter="10"
                :rules="[(v) => !!v || 'Text is required']"
                label="Text"
                required
                class="mb-2 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="emoji"
                variant="outlined"
                density="comfortable"
                label="Emoji (optional)"
                hint="Shown above the question text on the quiz page, e.g. 💊"
                persistent-hint
                class="mb-4 admin-field"
              ></v-text-field>

              <p class="admin-section-label">
                Applicable methods
                <span class="admin-section-label-hint"
                  >— used as the "Yes" answer's methods when this question has
                  no answer options below</span
                >
              </p>
              <div class="method-chip-picker">
                <button
                  v-for="method in allMethods"
                  :key="method.id"
                  type="button"
                  class="method-chip"
                  :class="{ active: applicableMethods.includes(method.id) }"
                  @click="toggleMethodId(applicableMethods, method.id)"
                >
                  {{ method.name }}
                </button>
                <p v-if="allMethods.length === 0" class="admin-option-empty">
                  No methods available yet — create one on the Methods tab.
                </p>
              </div>

              <v-text-field
                v-model.number="position"
                variant="outlined"
                density="comfortable"
                :rules="[
                  (v) =>
                    Number.isInteger(Number(v)) ||
                    'The value must be an integer number',
                ]"
                label="Position"
                required
                class="mb-2 mt-4 admin-field"
              ></v-text-field>

              <div class="admin-subsection">
                <p class="admin-subsection-label">Answer options (optional)</p>
                <p class="admin-subsection-hint">
                  Leave empty for a simple Yes/No question. Add options to
                  present the quiz-taker with several choices instead, each
                  carrying its own applicable methods.
                </p>

                <v-text-field
                  v-model="newOptionLabel"
                  variant="outlined"
                  density="compact"
                  label="Option label"
                  hide-details
                  class="admin-field mb-2"
                ></v-text-field>

                <div class="method-chip-picker method-chip-picker-compact">
                  <button
                    v-for="method in allMethods"
                    :key="method.id"
                    type="button"
                    class="method-chip method-chip-small"
                    :class="{ active: newOptionMethods.includes(method.id) }"
                    @click="toggleMethodId(newOptionMethods, method.id)"
                  >
                    {{ method.name }}
                  </button>
                </div>

                <div class="d-flex" style="gap: 8px">
                  <button
                    type="button"
                    class="pill-btn pill-btn-add"
                    @click="saveOption"
                  >
                    <v-icon size="16" class="mr-1">{{
                      editingOptionIndex === null ? "mdi-plus" : "mdi-check"
                    }}</v-icon>
                    {{
                      editingOptionIndex === null
                        ? "Add option"
                        : "Update option"
                    }}
                  </button>
                  <button
                    v-if="editingOptionIndex !== null"
                    type="button"
                    class="pill-btn pill-btn-outline pill-btn-danger pill-btn-small"
                    @click="cancelEditOption"
                  >
                    Cancel
                  </button>
                </div>

                <div class="admin-option-list">
                  <div
                    v-for="(option, index) in options"
                    :key="index"
                    class="option-card"
                    :class="{
                      'option-card-editing': editingOptionIndex === index,
                    }"
                  >
                    <div class="option-card-top">
                      <span class="option-card-label">{{ option.label }}</span>
                      <div class="d-flex" style="gap: 8px">
                        <v-icon
                          icon="mdi-pencil"
                          size="18"
                          color="primary"
                          @click="editOption(index)"
                        ></v-icon>
                        <v-icon
                          icon="mdi-delete"
                          size="18"
                          class="admin-option-delete"
                          @click="deleteOption(index)"
                        ></v-icon>
                      </div>
                    </div>
                    <div class="option-card-methods">
                      <span
                        v-for="(methodId, mIndex) in option.applicableMethods"
                        :key="mIndex"
                        class="method-tag"
                      >
                        {{ findMethodNameById(methodId) }}
                      </span>
                      <span
                        v-if="option.applicableMethods.length === 0"
                        class="admin-option-empty"
                        >No methods</span
                      >
                    </div>
                  </div>
                  <p v-if="options.length === 0" class="admin-option-empty">
                    No options added — this question will render as Yes/No.
                  </p>
                </div>
              </div>

              <div class="admin-actions">
                <button
                  type="button"
                  class="pill-btn pill-btn-outline"
                  @click="validate"
                >
                  <v-icon size="16" class="mr-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  Validate
                </button>

                <button
                  type="button"
                  class="pill-btn pill-btn-outline pill-btn-danger"
                  @click="reset"
                >
                  <v-icon size="16" class="mr-1">mdi-restore</v-icon>
                  Reset
                </button>

                <button
                  type="button"
                  class="pill-btn pill-btn-solid"
                  @click="save"
                >
                  <v-icon size="16" class="mr-1"
                    >mdi-content-save-outline</v-icon
                  >
                  Save
                </button>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="8">
        <v-card class="admin-card" elevation="1">
          <v-card-title class="admin-card-title">
            <v-icon class="mr-2" size="20">mdi-format-list-bulleted</v-icon>
            Questions
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <div v-if="sortedQuestions.length === 0" class="admin-option-empty">
              No questions yet — create one on the left.
            </div>
            <div class="question-list">
              <div
                v-for="item in sortedQuestions"
                :key="item.id"
                class="question-row-card"
              >
                <div class="question-row-badge">{{ item.position }}</div>
                <div class="question-row-body">
                  <div class="question-row-top">
                    <span v-if="item.emoji" class="question-row-emoji">{{
                      item.emoji
                    }}</span>
                    <span class="question-row-text">{{ item.text }}</span>
                  </div>
                  <div class="question-row-chips">
                    <template v-if="item.options && item.options.length > 0">
                      <span
                        v-for="(option, index) in item.options"
                        :key="index"
                        class="method-tag method-tag-secondary"
                      >
                        {{ option.label }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="method-tag method-tag-muted">Yes/No</span>
                      <span
                        v-for="(methodId, index) in item.applicableMethods"
                        :key="index"
                        class="method-tag"
                      >
                        {{ findMethodNameById(methodId) }}
                      </span>
                    </template>
                  </div>
                </div>
                <div class="question-row-actions">
                  <v-icon
                    color="primary"
                    icon="mdi-pencil"
                    size="small"
                    @click="editItem(item)"
                  ></v-icon>
                  <v-icon
                    icon="mdi-content-copy"
                    color="primary"
                    size="small"
                    @click="cloneItem(item)"
                  ></v-icon>
                  <v-icon
                    color="error"
                    icon="mdi-delete"
                    size="small"
                    @click="deleteItem(item)"
                  ></v-icon>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="pill-btn pill-btn-outline mt-3"
              @click="cleanQuestions()"
            >
              <v-icon size="16" class="mr-1">mdi-broom</v-icon>
              Clean questions after removing method
            </button>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QuestionDataService from "../services/QuestionDataService";
import MethodDataService from "../services/MethodDataService";

export default {
  name: "QuestionAdmin",
  data() {
    return {
      valid: true,
      editing: false,
      editingItem: "",
      allQuestions: [],
      allMethods: [],
      text: "",
      emoji: "",
      applicableMethods: [],
      position: 0,
      options: [],
      newOptionLabel: "",
      newOptionMethods: [],
      editingOptionIndex: null,
    };
  },
  async created() {
    this.allQuestions = await QuestionDataService.getAll();
    this.allMethods = await MethodDataService.getAll();
  },
  computed: {
    sortedQuestions() {
      return [...this.allQuestions].sort(
        (a, b) => (a.position || 0) - (b.position || 0),
      );
    },
  },
  methods: {
    toggleMethodId(list, id) {
      const index = list.indexOf(id);
      if (index > -1) {
        list.splice(index, 1);
      } else {
        list.push(id);
      }
    },
    async validate() {
      const { valid } = await this.$refs.form.validate();
      if (valid) alert("Form is valid");
    },
    reset() {
      this.editing = false;
      this.editingItem = "";
      this.text = "";
      this.emoji = "";
      this.applicableMethods = [];
      this.position = 0;
      this.options = [];
      this.newOptionLabel = "";
      this.newOptionMethods = [];
      this.editingOptionIndex = null;
    },
    saveOption() {
      if (!this.newOptionLabel) return;
      if (this.editingOptionIndex !== null) {
        this.options[this.editingOptionIndex] = {
          label: this.newOptionLabel,
          applicableMethods: this.newOptionMethods,
        };
        this.editingOptionIndex = null;
      } else {
        this.options.push({
          label: this.newOptionLabel,
          applicableMethods: this.newOptionMethods,
        });
      }
      this.newOptionLabel = "";
      this.newOptionMethods = [];
    },
    editOption(index) {
      const option = this.options[index];
      this.newOptionLabel = option.label;
      this.newOptionMethods = [...option.applicableMethods];
      this.editingOptionIndex = index;
    },
    cancelEditOption() {
      this.newOptionLabel = "";
      this.newOptionMethods = [];
      this.editingOptionIndex = null;
    },
    deleteOption(index) {
      this.options.splice(index, 1);
      if (this.editingOptionIndex === index) {
        this.cancelEditOption();
      } else if (
        this.editingOptionIndex !== null &&
        index < this.editingOptionIndex
      ) {
        this.editingOptionIndex -= 1;
      }
    },
    async save() {
      const { valid } = await this.$refs.form.validate();
      if (valid) {
        const question = {
          text: this.text,
          emoji: this.emoji,
          applicableMethods: this.applicableMethods,
          position: this.position,
          options: this.options,
        };
        if (this.editing && this.editingItem && this.editingItem.id) {
          var updateResponse = await QuestionDataService.update(
            this.editingItem.id,
            question,
          );
          if (updateResponse) {
            this.$swal("Question updated successfully");
          } else {
            this.$swal("Error updating question", "", "error");
          }
        } else {
          var createResponse = await QuestionDataService.create(question);
          console.log("createResponse:", createResponse);
          if (createResponse) {
            this.$swal("Question created successfully");
          }
        }
        this.reset();
        this.allQuestions = await QuestionDataService.getAll();
      }
    },
    editItem(item) {
      this.editing = true;
      this.editingItem = item;
      this.text = item.text || "";
      this.emoji = item.emoji || "";
      this.applicableMethods = item.applicableMethods || [];
      this.position = item.position || 0;
      this.options = item.options || [];
      this.newOptionLabel = "";
      this.newOptionMethods = [];
      this.editingOptionIndex = null;
    },
    async deleteItem(item) {
      console.log(item);
      this.$swal({
        title: "Are you sure you want to delete this item?",
        showCancelButton: true,
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (result.isConfirmed) {
          var deleteResponse = await QuestionDataService.delete(item.id);
          if (deleteResponse) {
            this.$swal("Question deleted successfully");
            this.allQuestions = await QuestionDataService.getAll();
          } else {
            this.$swal("Error deleting question", "", "error");
          }
        } else if (result.isDismissed) {
          this.$swal("Cancelled", "", "info");
        }
      });
    },
    async cloneItem(item) {
      const clonedQuestion = {
        text: `${item.text} - Copy`,
        emoji: item.emoji || "",
        applicableMethods: item.applicableMethods
          ? [...item.applicableMethods]
          : [],
        position: (item.position || 0) + 1, // Increment position to avoid conflicts
        options: item.options
          ? item.options.map((option) => ({
              label: option.label,
              applicableMethods: [...option.applicableMethods],
            }))
          : [],
      };

      const createResponse = await QuestionDataService.create(clonedQuestion);
      if (createResponse) {
        this.$swal("Question cloned successfully");
        this.allQuestions = await QuestionDataService.getAll();
      } else {
        this.$swal("Error cloning question", "", "error");
      }
    },
    findMethodNameById(id) {
      const method = this.allMethods.find((method) => method.id === id);
      return method ? method.name || "" : "";
    },
    cleanQuestions() {
      this.allQuestions.forEach(async (question) => {
        var { id, text, emoji, applicableMethods, position, options } =
          question;

        applicableMethods.forEach(async (methodId) => {
          const method = this.allMethods.find(
            (method) => method.id === methodId,
          );
          if (!method) {
            console.log("method not found:", methodId);
            // remove method if not found from question
            const index = applicableMethods.indexOf(methodId);
            console.log(index);
            if (index > -1) {
              applicableMethods.splice(index, 1);
            }
          }
        });

        const questionToSave = {
          text,
          emoji: emoji || "",
          applicableMethods,
          position,
          options: options || [],
        };
        var updateResponse = await QuestionDataService.update(
          id,
          questionToSave,
        );
        if (updateResponse) {
          console.log("Question updated successfully:", id);
        } else {
          console.log("Error updating question:", id);
        }
      });
    },
  },
};
</script>

<style scoped>
.admin-page {
  padding-bottom: 24px;
}

.admin-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.94) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1) !important;
}

.admin-card-title {
  display: flex;
  align-items: center;
  font-family: "Montserrat", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #3d80e8;
  padding: 16px 20px;
}

.admin-field :deep(.v-field) {
  border-radius: 12px;
}

.admin-field :deep(.v-field--focused .v-field__outline) {
  color: #3d80e8;
}

.admin-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a92a3;
  margin: 20px 0 10px;
}

.admin-section-label-hint {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  color: #a4abb8;
}

.method-chip-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.method-chip-picker-compact {
  margin-bottom: 10px;
}

.method-chip {
  border: 2px solid rgba(61, 128, 232, 0.35);
  background: #ffffff;
  color: #3d80e8;
  border-radius: 99px;
  padding: 8px 16px;
  font-weight: 700;
  font-size: 0.82rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: #3d80e8;
    border-color: #3d80e8;
    color: #ffffff;
  }

  &:hover:not(.active) {
    background: rgba(61, 128, 232, 0.08);
  }
}

.method-chip-small {
  padding: 5px 12px;
  font-size: 0.76rem;
}

.pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  padding: 10px 22px;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}

.pill-btn-outline {
  background: transparent;
  border: 2px solid #3d80e8;
  color: #3d80e8;

  &:hover {
    background: rgba(61, 128, 232, 0.08);
  }
}

.pill-btn-danger {
  border-color: #eb63eb;
  color: #eb63eb;

  &:hover {
    background: rgba(235, 99, 235, 0.08);
  }
}

.pill-btn-solid {
  background: #3d80e8;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(61, 128, 232, 0.3);

  &:hover {
    background: #326fd1;
  }
}

.pill-btn-add {
  border: 2px dashed rgba(61, 128, 232, 0.4);
  color: #3d80e8;
  background: transparent;
  padding: 8px 18px;
  font-size: 0.8rem;

  &:hover {
    background: rgba(61, 128, 232, 0.08);
  }
}

.pill-btn-small {
  padding: 6px 14px;
  font-size: 0.75rem;
}

.option-card-editing {
  border-left-color: #eba614;
  box-shadow: 0 0 0 2px rgba(235, 166, 20, 0.2);
}

.admin-subsection {
  background: #f4f6fa;
  border: 1px solid #e3e7ef;
  border-radius: 10px;
  padding: 16px;
  margin: 8px 0 20px;
}

.admin-subsection-label {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2c3444;
  margin-bottom: 2px;
}

.admin-subsection-hint {
  color: #6b7280;
  font-size: 0.8rem;
  margin-bottom: 14px;
}

.admin-option-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.option-card {
  background: #ffffff;
  border: 1px solid #e3e7ef;
  border-left: 3px solid #3d80e8;
  border-radius: 10px;
  padding: 10px 14px;
}

.option-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.option-card-label {
  font-weight: 700;
  color: #2c3444;
  font-size: 0.9rem;
}

.option-card-methods {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.method-tag {
  display: inline-block;
  background: rgba(61, 128, 232, 0.1);
  color: #3d80e8;
  border-radius: 99px;
  padding: 3px 10px;
  font-size: 0.72rem;
  font-weight: 700;
}

.method-tag-secondary {
  background: rgba(235, 166, 20, 0.14);
  color: #b3790e;
}

.method-tag-muted {
  background: #eef1f6;
  color: #6b7280;
}

.admin-option-delete {
  cursor: pointer;
  color: #9aa1ae;
}

.admin-option-delete:hover {
  color: #d32f2f;
}

.admin-option-empty {
  color: #9aa1ae;
  font-size: 0.82rem;
  font-style: italic;
  margin: 0;
}

.admin-actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.question-row-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 12px 14px;
}

.question-row-badge {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(61, 128, 232, 0.12);
  color: #3d80e8;
  font-weight: 700;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-row-body {
  flex: 1;
  min-width: 0;
}

.question-row-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.question-row-emoji {
  font-size: 1.1rem;
}

.question-row-text {
  font-weight: 600;
  color: #2c3444;
  font-size: 0.92rem;
}

.question-row-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.question-row-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}
</style>
